import { supabase } from '../lib/supabase';
import {
  DEFAULT_ONLINE_LEAGUE_CONFIG,
  ONLINE_DIVISIONS,
  type OnlineDivision,
  type OnlineLeagueConfig,
} from '../online/league';
import {
  ONLINE_LEADERBOARD_CACHE_MS,
  newestTimestamp,
  normalizeLeaderboardPage,
} from '../online/rankings';
import type { MatchResult } from '../types';

export interface OnlineSeasonRow {
  id: string;
  name: string;
  starts_at: string;
  ends_at: string;
  status: 'UPCOMING' | 'ACTIVE' | 'SETTLING' | 'FINISHED';
  config_version: string;
}

interface OnlineConfigRow {
  version: string;
  engine_version: string;
  season_duration_days: number;
  initial_division: OnlineDivision;
  initial_league_points: number;
  win_points: number;
  draw_points: number;
  loss_points: number;
  abandonment_points: number;
  promotion_thresholds: Partial<Record<OnlineDivision, number>>;
  relegation_thresholds: Partial<Record<OnlineDivision, number>>;
  promoted_starting_points: number;
  relegated_starting_points: number;
  minimum_matches_before_promotion: number;
  placement_matches: number;
  rating_base: number;
  rating_floor: number;
  rating_k_factor: number;
  placement_k_factor: number;
  max_provisional_rank_diff: number;
  matchmaking_ticket_ttl_seconds: number;
  matchmaking_base_mmr_tolerance: number;
  matchmaking_mmr_tolerance_per_minute: number;
  matchmaking_adjacent_division_wait_seconds: number;
  matchmaking_rematch_cooldown_minutes: number;
  matchmaking_max_squad_power_diff: number;
  worker_lease_seconds: number;
  max_simulation_attempts: number;
  replay_frame_interval_seconds: number;
}

interface OnlineProfileRow {
  id: string;
  public_id: string;
  alias: string;
  active_squad_snapshot_id: string | null;
}

interface OnlineRankRow {
  division: OnlineDivision;
  division_points: number;
  wins: number;
  draws: number;
  losses: number;
  best_division: OnlineDivision;
  last_match_at: string | null;
}

interface OnlineSnapshotRow {
  validation_status: 'PENDING' | 'VALIDATING' | 'VALID' | 'REJECTED';
  squad_power: number | null;
  validation_error: string | null;
  validated_at: string | null;
}

interface OnlineLeaderboardRow {
  season_id: string;
  season_name: string;
  season_status: OnlineSeasonRow['status'];
  season_starts_at: string;
  season_ends_at: string;
  public_player_id: string;
  alias: string;
  division: OnlineDivision;
  division_points: number;
  wins: number;
  draws: number;
  losses: number;
  best_division: OnlineDivision;
  matches_played: number;
  win_rate: number | string;
  updated_at: string;
  global_position: number;
  division_position: number;
}

export type OnlineTicketStatus = 'SEARCHING' | 'MATCHED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED' | 'FAILED';
export type OnlineMatchStatus = 'QUEUED' | 'MATCHED' | 'SIMULATING' | 'READY' | 'SETTLED' | 'VOID' | 'FAILED';

export interface OnlineQueueState {
  ticketId: string;
  ticketStatus: OnlineTicketStatus;
  enteredAt: string;
  expiresAt: string;
  matchId?: string;
  matchStatus?: OnlineMatchStatus;
  opponentAlias?: string;
  isHome?: boolean;
  homeGoals?: number;
  awayGoals?: number;
  pointDelta?: number;
  settledAt?: string;
  failureCode?: string;
}

export interface OnlineMatchHistoryItem {
  id: string;
  seasonId: string;
  homePublicPlayerId: string;
  homeAlias: string;
  awayPublicPlayerId: string;
  awayAlias: string;
  homeGoals: number;
  awayGoals: number;
  status: 'SETTLED';
  engineVersion: string;
  rulesVersion: string;
  settledAt: string;
}

export interface OnlineReplayEnvelope {
  schemaVersion: number;
  frameIntervalSeconds: number;
  matchResult: MatchResult;
}

export interface OnlineLeaderboardEntry {
  seasonId: string;
  seasonName: string;
  seasonStatus: OnlineSeasonRow['status'];
  seasonStartsAt: string;
  seasonEndsAt: string;
  publicPlayerId: string;
  alias: string;
  division: OnlineDivision;
  divisionPoints: number;
  wins: number;
  draws: number;
  losses: number;
  bestDivision: OnlineDivision;
  matchesPlayed: number;
  winRate: number;
  updatedAt: string;
  globalPosition: number;
  divisionPosition: number;
}

export interface OnlineLeaderboardPage {
  items: OnlineLeaderboardEntry[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  fetchedAt: string;
  updatedAt: string | null;
}

export interface OnlinePublicProfile {
  publicPlayerId: string;
  alias: string;
  memberSince: string;
  bestDivision: OnlineDivision;
  wins: number;
  draws: number;
  losses: number;
  matchesPlayed: number;
  winRate: number;
  seasonsPlayed: number;
  lastMatchAt: string | null;
  updatedAt: string;
}

export type OnlineTournamentStatus = 'DRAFT' | 'REGISTRATION' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type OnlineTournamentEntryStatus = 'REGISTERED' | 'CONFIRMED' | 'ELIMINATED' | 'WITHDRAWN' | 'DISQUALIFIED' | 'FINALIST' | 'CHAMPION';
export type OnlineTournamentMatchStatus = 'WAITING' | 'PENDING' | 'SIMULATING' | 'SETTLED' | 'VOID' | 'FAILED';

interface OnlineTournamentListingRow {
  id: string;
  season_id: string;
  season_name: string;
  definition_key: string;
  name: string;
  tournament_type: 'KNOCKOUT';
  status: OnlineTournamentStatus;
  bracket_size: number;
  allow_byes: boolean;
  minimum_division: OnlineDivision | null;
  maximum_division: OnlineDivision | null;
  registration_opens_at: string;
  registration_closes_at: string;
  starts_at: string;
  round_deadline_seconds: number;
  completed_at: string | null;
  winner_public_player_id: string | null;
  winner_alias: string | null;
  runner_up_public_player_id: string | null;
  runner_up_alias: string | null;
  participant_count: number;
}

export interface OnlineTournamentListing {
  id: string;
  seasonId: string;
  seasonName: string;
  definitionKey: string;
  name: string;
  status: OnlineTournamentStatus;
  bracketSize: number;
  allowByes: boolean;
  minimumDivision: OnlineDivision | null;
  maximumDivision: OnlineDivision | null;
  registrationOpensAt: string;
  registrationClosesAt: string;
  startsAt: string;
  roundDeadlineSeconds: number;
  completedAt: string | null;
  winnerPublicPlayerId: string | null;
  winnerAlias: string | null;
  runnerUpPublicPlayerId: string | null;
  runnerUpAlias: string | null;
  participantCount: number;
}

interface OnlineTournamentBracketRow {
  id: string;
  tournament_id: string;
  round_number: number;
  match_number: number;
  status: OnlineTournamentMatchStatus;
  home_goals: number | null;
  away_goals: number | null;
  home_shootout_goals: number | null;
  away_shootout_goals: number | null;
  resolution: 'REGULATION' | 'DETERMINISTIC_PENALTIES' | 'SYSTEM_FORFEIT' | null;
  settled_at: string | null;
  home_public_player_id: string | null;
  home_alias: string | null;
  away_public_player_id: string | null;
  away_alias: string | null;
  winner_public_player_id: string | null;
}

export interface OnlineTournamentMatch {
  id: string;
  tournamentId: string;
  roundNumber: number;
  matchNumber: number;
  status: OnlineTournamentMatchStatus;
  homeGoals: number | null;
  awayGoals: number | null;
  homeShootoutGoals: number | null;
  awayShootoutGoals: number | null;
  resolution: OnlineTournamentBracketRow['resolution'];
  settledAt: string | null;
  homePublicPlayerId: string | null;
  homeAlias: string | null;
  awayPublicPlayerId: string | null;
  awayAlias: string | null;
  winnerPublicPlayerId: string | null;
}

export interface OnlineTournamentPersonalState {
  entryStatus?: OnlineTournamentEntryStatus;
  entryId?: string;
  seedPosition?: number;
}

export interface OnlinePortalData {
  season: OnlineSeasonRow | null;
  config: OnlineLeagueConfig;
  profile: null | {
    id: string;
    publicId: string;
    alias: string;
    activeSquadSnapshotId: string | null;
  };
  rank: null | {
    division: OnlineDivision;
    divisionPoints: number;
    wins: number;
    draws: number;
    losses: number;
    bestDivision: OnlineDivision;
    lastMatchAt: string | null;
  };
  snapshot: null | {
    validationStatus: OnlineSnapshotRow['validation_status'];
    squadPower: number | null;
    validationError: string | null;
    validatedAt: string | null;
  };
}

const configColumns = [
  'version', 'engine_version', 'season_duration_days', 'initial_division', 'initial_league_points',
  'win_points', 'draw_points', 'loss_points', 'abandonment_points', 'promotion_thresholds',
  'relegation_thresholds', 'promoted_starting_points', 'relegated_starting_points',
  'minimum_matches_before_promotion', 'placement_matches', 'rating_base', 'rating_floor',
  'rating_k_factor', 'placement_k_factor', 'max_provisional_rank_diff',
  'matchmaking_ticket_ttl_seconds', 'matchmaking_base_mmr_tolerance',
  'matchmaking_mmr_tolerance_per_minute', 'matchmaking_adjacent_division_wait_seconds',
  'matchmaking_rematch_cooldown_minutes', 'matchmaking_max_squad_power_diff',
  'worker_lease_seconds', 'max_simulation_attempts', 'replay_frame_interval_seconds',
].join(', ');

function client() {
  if (!supabase) throw new Error('Supabase no está configurado; el modo Online necesita una cuenta conectada.');
  return supabase;
}

const leaderboardColumns = [
  'season_id', 'season_name', 'season_status', 'season_starts_at', 'season_ends_at',
  'public_player_id', 'alias', 'division', 'division_points', 'wins', 'draws', 'losses',
  'best_division', 'matches_played', 'win_rate', 'updated_at', 'global_position',
  'division_position',
].join(', ');

const leaderboardCache = new Map<string, { expiresAt: number; value: OnlineLeaderboardPage }>();

function asLeaderboardEntry(row: OnlineLeaderboardRow): OnlineLeaderboardEntry {
  return {
    seasonId: row.season_id,
    seasonName: row.season_name,
    seasonStatus: row.season_status,
    seasonStartsAt: row.season_starts_at,
    seasonEndsAt: row.season_ends_at,
    publicPlayerId: row.public_player_id,
    alias: row.alias,
    division: row.division,
    divisionPoints: row.division_points,
    wins: row.wins,
    draws: row.draws,
    losses: row.losses,
    bestDivision: row.best_division,
    matchesPlayed: row.matches_played,
    winRate: Number(row.win_rate),
    updatedAt: row.updated_at,
    globalPosition: Number(row.global_position),
    divisionPosition: Number(row.division_position),
  };
}

function asConfig(row: OnlineConfigRow | null): OnlineLeagueConfig {
  if (!row) return DEFAULT_ONLINE_LEAGUE_CONFIG;
  return {
    version: row.version,
    engineVersion: row.engine_version,
    seasonDurationDays: row.season_duration_days,
    initialDivision: row.initial_division,
    initialLeaguePoints: row.initial_league_points,
    winPoints: row.win_points,
    drawPoints: row.draw_points,
    lossPoints: row.loss_points,
    abandonmentPoints: row.abandonment_points,
    promotionThresholdByDivision: row.promotion_thresholds,
    relegationThresholdByDivision: row.relegation_thresholds,
    promotedStartingPoints: row.promoted_starting_points,
    relegatedStartingPoints: row.relegated_starting_points,
    minimumMatchesBeforePromotion: row.minimum_matches_before_promotion,
    placementMatches: row.placement_matches,
    ratingBase: row.rating_base,
    ratingFloor: row.rating_floor,
    ratingKFactor: row.rating_k_factor,
    placementKFactor: row.placement_k_factor,
    maxProvisionalRankDiff: row.max_provisional_rank_diff,
    matchmakingTicketTtlSeconds: row.matchmaking_ticket_ttl_seconds,
    matchmakingBaseMmrTolerance: row.matchmaking_base_mmr_tolerance,
    matchmakingMmrTolerancePerMinute: row.matchmaking_mmr_tolerance_per_minute,
    matchmakingAdjacentDivisionWaitSeconds: row.matchmaking_adjacent_division_wait_seconds,
    matchmakingRematchCooldownMinutes: row.matchmaking_rematch_cooldown_minutes,
    matchmakingMaxSquadPowerDiff: row.matchmaking_max_squad_power_diff,
    workerLeaseSeconds: row.worker_lease_seconds,
    maxSimulationAttempts: row.max_simulation_attempts,
    replayFrameIntervalSeconds: row.replay_frame_interval_seconds,
  };
}

export async function getOnlinePortal(): Promise<OnlinePortalData> {
  const database = client();
  const now = new Date().toISOString();
  const { data: seasonData, error: seasonError } = await database
    .from('online_seasons')
    .select('id, name, starts_at, ends_at, status, config_version')
    .eq('status', 'ACTIVE')
    .lte('starts_at', now)
    .gt('ends_at', now)
    .order('starts_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (seasonError) throw new Error('No se ha podido cargar la temporada online. ¿Has aplicado la migración 002?');
  const season = seasonData as OnlineSeasonRow | null;

  const configQuery = database.from('online_league_configs').select(configColumns);
  const { data: configData, error: configError } = season
    ? await configQuery.eq('version', season.config_version).single()
    : await configQuery.eq('is_active', true).limit(1).maybeSingle();
  if (configError) throw new Error('No se ha podido cargar la configuración competitiva.');

  const { data: profileData, error: profileError } = await database
    .from('online_profiles')
    .select('id, public_id, alias, active_squad_snapshot_id')
    .limit(1)
    .maybeSingle();
  if (profileError) throw new Error('No se ha podido cargar tu perfil online.');
  const profile = profileData as OnlineProfileRow | null;

  let rank: OnlineRankRow | null = null;
  let snapshot: OnlineSnapshotRow | null = null;
  if (profile && season) {
    const { data: rankData, error: rankError } = await database
      .from('online_rank_states')
      .select('division, division_points, wins, draws, losses, best_division, last_match_at')
      .eq('profile_id', profile.id)
      .eq('season_id', season.id)
      .maybeSingle();
    if (rankError) throw new Error('No se ha podido cargar tu clasificación online.');
    rank = rankData as OnlineRankRow | null;
  }
  if (profile?.active_squad_snapshot_id) {
    const { data: snapshotData, error: snapshotError } = await database
      .from('online_squad_snapshots')
      .select('validation_status, squad_power, validation_error, validated_at')
      .eq('id', profile.active_squad_snapshot_id)
      .single();
    if (snapshotError) throw new Error('No se ha podido comprobar la plantilla online. ¿Has aplicado la migración 003?');
    snapshot = snapshotData as OnlineSnapshotRow;
  }

  return {
    season,
    config: asConfig(configData as OnlineConfigRow | null),
    profile: profile ? {
      id: profile.id,
      publicId: profile.public_id,
      alias: profile.alias,
      activeSquadSnapshotId: profile.active_squad_snapshot_id,
    } : null,
    rank: rank ? {
      division: rank.division,
      divisionPoints: rank.division_points,
      wins: rank.wins,
      draws: rank.draws,
      losses: rank.losses,
      bestDivision: rank.best_division,
      lastMatchAt: rank.last_match_at,
    } : null,
    snapshot: snapshot ? {
      validationStatus: snapshot.validation_status,
      squadPower: snapshot.squad_power,
      validationError: snapshot.validation_error,
      validatedAt: snapshot.validated_at,
    } : null,
  };
}

export async function listOnlineSeasons(): Promise<OnlineSeasonRow[]> {
  const { data, error } = await client()
    .from('online_seasons')
    .select('id, name, starts_at, ends_at, status, config_version')
    .order('starts_at', { ascending: false });
  if (error) throw new Error('No se han podido cargar las temporadas online.');
  return (data ?? []) as OnlineSeasonRow[];
}

export async function listOnlineLeaderboard(options: {
  seasonId: string;
  division?: OnlineDivision;
  page?: number;
  pageSize?: number;
  force?: boolean;
}): Promise<OnlineLeaderboardPage> {
  if (!options.seasonId) throw new Error('Selecciona una temporada para consultar la clasificación.');
  if (options.division && !ONLINE_DIVISIONS.includes(options.division)) {
    throw new Error('La división solicitada no es válida.');
  }
  const pagination = normalizeLeaderboardPage(options.page ?? 1, options.pageSize);
  const cacheKey = [options.seasonId, options.division ?? 'GLOBAL', pagination.page, pagination.pageSize].join(':');
  const cached = leaderboardCache.get(cacheKey);
  if (!options.force && cached && cached.expiresAt > Date.now()) return cached.value;

  let query = client()
    .from('online_leaderboard')
    .select(leaderboardColumns, { count: 'exact' })
    .eq('season_id', options.seasonId);
  if (options.division) query = query.eq('division', options.division);
  const orderColumn = options.division ? 'division_position' : 'global_position';
  const { data, error, count } = await query
    .order(orderColumn, { ascending: true })
    .range(pagination.from, pagination.to);
  if (error) throw new Error('No se ha podido cargar la clasificación online. ¿Has aplicado la migración 004?');
  const items = ((data ?? []) as unknown as OnlineLeaderboardRow[]).map(asLeaderboardEntry);
  const total = count ?? items.length;
  const value: OnlineLeaderboardPage = {
    items,
    page: pagination.page,
    pageSize: pagination.pageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pagination.pageSize)),
    fetchedAt: new Date().toISOString(),
    updatedAt: newestTimestamp(items.map((item) => item.updatedAt)),
  };
  leaderboardCache.set(cacheKey, { expiresAt: Date.now() + ONLINE_LEADERBOARD_CACHE_MS, value });
  return value;
}

export function clearOnlineLeaderboardCache(): void {
  leaderboardCache.clear();
}

function asTournamentListing(row: OnlineTournamentListingRow): OnlineTournamentListing {
  return {
    id: row.id,
    seasonId: row.season_id,
    seasonName: row.season_name,
    definitionKey: row.definition_key,
    name: row.name,
    status: row.status,
    bracketSize: row.bracket_size,
    allowByes: row.allow_byes,
    minimumDivision: row.minimum_division,
    maximumDivision: row.maximum_division,
    registrationOpensAt: row.registration_opens_at,
    registrationClosesAt: row.registration_closes_at,
    startsAt: row.starts_at,
    roundDeadlineSeconds: row.round_deadline_seconds,
    completedAt: row.completed_at,
    winnerPublicPlayerId: row.winner_public_player_id,
    winnerAlias: row.winner_alias,
    runnerUpPublicPlayerId: row.runner_up_public_player_id,
    runnerUpAlias: row.runner_up_alias,
    participantCount: Number(row.participant_count),
  };
}

function asTournamentMatch(row: OnlineTournamentBracketRow): OnlineTournamentMatch {
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    roundNumber: Number(row.round_number),
    matchNumber: Number(row.match_number),
    status: row.status,
    homeGoals: row.home_goals,
    awayGoals: row.away_goals,
    homeShootoutGoals: row.home_shootout_goals,
    awayShootoutGoals: row.away_shootout_goals,
    resolution: row.resolution,
    settledAt: row.settled_at,
    homePublicPlayerId: row.home_public_player_id,
    homeAlias: row.home_alias,
    awayPublicPlayerId: row.away_public_player_id,
    awayAlias: row.away_alias,
    winnerPublicPlayerId: row.winner_public_player_id,
  };
}

export async function listOnlineTournaments(seasonId?: string): Promise<OnlineTournamentListing[]> {
  let query = client()
    .from('online_tournament_listings')
    .select('id, season_id, season_name, definition_key, name, tournament_type, status, bracket_size, allow_byes, minimum_division, maximum_division, registration_opens_at, registration_closes_at, starts_at, round_deadline_seconds, completed_at, winner_public_player_id, winner_alias, runner_up_public_player_id, runner_up_alias, participant_count')
    .order('starts_at', { ascending: true });
  if (seasonId) query = query.eq('season_id', seasonId);
  const { data, error } = await query;
  if (error) throw new Error('No se han podido cargar los torneos online. ¿Has aplicado la migración 005?');
  return ((data ?? []) as unknown as OnlineTournamentListingRow[]).map(asTournamentListing);
}

export async function getOnlineTournamentBracket(tournamentId: string): Promise<OnlineTournamentMatch[]> {
  const { data, error } = await client()
    .from('online_tournament_bracket')
    .select('id, tournament_id, round_number, match_number, status, home_goals, away_goals, home_shootout_goals, away_shootout_goals, resolution, settled_at, home_public_player_id, home_alias, away_public_player_id, away_alias, winner_public_player_id')
    .eq('tournament_id', tournamentId)
    .order('round_number', { ascending: true })
    .order('match_number', { ascending: true });
  if (error) throw new Error('No se ha podido cargar el cuadro del torneo.');
  return ((data ?? []) as unknown as OnlineTournamentBracketRow[]).map(asTournamentMatch);
}

export async function getOnlineTournamentState(tournamentId: string): Promise<OnlineTournamentPersonalState> {
  const { data, error } = await client().rpc('get_online_tournament_state', { p_tournament_id: tournamentId });
  if (error) throw new Error('No se ha podido consultar tu inscripción al torneo.');
  return (data ?? {}) as OnlineTournamentPersonalState;
}

export async function joinOnlineTournament(tournamentId: string): Promise<void> {
  const { error } = await client().rpc('join_online_tournament', { p_tournament_id: tournamentId });
  if (error) throw new Error(error.message || 'No se ha podido formalizar la inscripción.');
}

export async function withdrawOnlineTournament(tournamentId: string): Promise<void> {
  const { error } = await client().rpc('withdraw_online_tournament', { p_tournament_id: tournamentId });
  if (error) throw new Error(error.message || 'No se ha podido retirar la inscripción.');
}

export async function getOnlinePublicProfile(publicPlayerId: string): Promise<OnlinePublicProfile> {
  const { data, error } = await client().rpc('get_online_public_profile', {
    p_public_player_id: publicPlayerId,
  });
  if (error) throw new Error('No se ha podido cargar el perfil público. ¿Has aplicado la migración 004?');
  if (!data || typeof data !== 'object') throw new Error('El perfil online solicitado no existe.');
  return data as OnlinePublicProfile;
}

export async function listOnlineProfileSeasonHistory(
  publicPlayerId: string,
  page = 1,
  pageSize = 8,
): Promise<OnlineLeaderboardPage> {
  const pagination = normalizeLeaderboardPage(page, pageSize);
  const { data, error, count } = await client()
    .from('online_leaderboard')
    .select(leaderboardColumns, { count: 'exact' })
    .eq('public_player_id', publicPlayerId)
    .order('season_starts_at', { ascending: false })
    .range(pagination.from, pagination.to);
  if (error) throw new Error('No se ha podido cargar el historial de temporadas.');
  const items = ((data ?? []) as unknown as OnlineLeaderboardRow[]).map(asLeaderboardEntry);
  const total = count ?? items.length;
  return {
    items,
    page: pagination.page,
    pageSize: pagination.pageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pagination.pageSize)),
    fetchedAt: new Date().toISOString(),
    updatedAt: newestTimestamp(items.map((item) => item.updatedAt)),
  };
}

export async function activateOnlineProfile(gameSaveId: string, alias: string): Promise<void> {
  const normalizedAlias = alias.trim();
  if (normalizedAlias.length < 3 || normalizedAlias.length > 24) {
    throw new Error('El alias debe tener entre 3 y 24 caracteres.');
  }
  const { error } = await client().rpc('activate_online_profile', {
    p_game_save_id: gameSaveId,
    p_alias: normalizedAlias,
  });
  if (error) {
    if (error.code === '23505') throw new Error('Ese alias online ya está en uso.');
    throw new Error(error.message || 'No se ha podido vincular la plantilla online.');
  }
}

export async function joinOnlineQueue(): Promise<void> {
  const { error } = await client().rpc('join_online_queue', {
    p_region: 'EU',
    p_competition: 'LEAGUE',
  });
  if (error) {
    if (error.message.includes('not validated')) throw new Error('El worker todavía está validando la plantilla congelada.');
    if (error.message.includes('wait before')) throw new Error('Espera unos segundos antes de iniciar otra búsqueda.');
    throw new Error(error.message || 'No se ha podido entrar en la cola online.');
  }
}

export async function cancelOnlineQueue(): Promise<void> {
  const { error } = await client().rpc('cancel_online_queue', { p_competition: 'LEAGUE' });
  if (error) throw new Error(error.message || 'No se ha podido cancelar la búsqueda.');
}

export async function getOnlineQueueState(): Promise<OnlineQueueState | null> {
  const { data, error } = await client().rpc('get_online_queue_state');
  if (error) throw new Error('No se ha podido actualizar el estado del matchmaking.');
  return data ? data as OnlineQueueState : null;
}

export async function listOnlineMatchHistory(publicPlayerId: string, limit = 10): Promise<OnlineMatchHistoryItem[]> {
  const safeLimit = Math.max(1, Math.min(50, Math.floor(limit)));
  const { data, error } = await client()
    .from('online_match_history')
    .select('id, season_id, home_public_player_id, home_alias, away_public_player_id, away_alias, home_goals, away_goals, status, engine_version, rules_version, settled_at')
    .or(`home_public_player_id.eq.${publicPlayerId},away_public_player_id.eq.${publicPlayerId}`)
    .order('settled_at', { ascending: false })
    .limit(safeLimit);
  if (error) throw new Error('No se ha podido cargar el historial online.');
  return (data ?? []).map((row) => ({
    id: row.id as string,
    seasonId: row.season_id as string,
    homePublicPlayerId: row.home_public_player_id as string,
    homeAlias: row.home_alias as string,
    awayPublicPlayerId: row.away_public_player_id as string,
    awayAlias: row.away_alias as string,
    homeGoals: row.home_goals as number,
    awayGoals: row.away_goals as number,
    status: 'SETTLED',
    engineVersion: row.engine_version as string,
    rulesVersion: row.rules_version as string,
    settledAt: row.settled_at as string,
  }));
}

export async function getOnlineReplay(matchId: string): Promise<OnlineReplayEnvelope> {
  const { data, error } = await client()
    .from('online_match_replays')
    .select('replay')
    .eq('match_id', matchId)
    .single();
  if (error || !data) throw new Error('No se ha podido cargar el replay autoritativo.');
  const replay = data.replay as unknown as OnlineReplayEnvelope;
  if (replay?.schemaVersion !== 1 || !replay.matchResult || !Array.isArray(replay.matchResult.frames)) {
    throw new Error('El replay guardado usa un formato incompatible.');
  }
  return replay;
}

export function subscribeToOnlineTicket(profileId: string, onChange: () => void): () => void {
  const database = client();
  const channel = database.channel(`online-ticket-${profileId}`)
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'online_matchmaking_tickets',
      filter: `profile_id=eq.${profileId}`,
    }, onChange)
    .subscribe();
  return () => { void database.removeChannel(channel); };
}
